import React from 'react';

import styled from 'styled-components';

import { AIInsightGroup } from './types';


const Card = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 16px 20px;
  border-radius: 4px;
  border-top-right-radius: 0;
  position: relative;
  border: 1px solid var(--light-gray);
  border-top: none;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -1px;
    right: 0;
    height: 4px;
    border-radius: 4px 0 0 0;
  }

  &.pricing-analysis::before {
    background-image: linear-gradient(to left, var(--mint-green), var(--positive-green));
  }
  &.fee-analysis::before {
    background-image: linear-gradient(to left, var(--warm-gold), var(--inactive-orange));
  }
`;

const IconBadge = styled.div`
  position: absolute;
  top: 0px;
  right: -1px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.pricing-analysis {
    background-image: linear-gradient(to bottom, var(--mint-green), var(--positive-green));
  }
  &.fee-analysis {
    background-image: linear-gradient(to bottom, var(--light-gold), var(--inactive-orange));
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const CardTitle = styled.div`
  font-size: 16px;
  font-family: var(--font-bold);
  margin-bottom: 8px;
`;

const SectionWrapper = styled.div`
  padding-top: 10px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  margin-bottom: 4px;
`;

const InsightList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-flow: column;
  gap: 4px;
  font-size: 13px;
  color: var(--text-secondary);
`;

const InsightItem = styled.li`
  line-height: 21px;
  padding-left: 12px;
  position: relative;

  &::before {
    content: '·';
    position: absolute;
    left: 0;
  }
`;

const EmptyText = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

type Props = {
  insights: AIInsightGroup;
  className: string;
  icon: string;
  title: string;
}

const SECTIONS = [
  { key: 'opportunities' as const, title: 'Missed Revenue Opportunity' },
  { key: 'threats' as const, title: 'Potential Occupancy and Revenue Threat' },
];

const GroupedInsightCard = ({ insights, className, icon, title }: Props) => {
  const hasAnyItems = SECTIONS.some(s => (insights[s.key] || []).length > 0);

  return (
    <Card className={className} data-testid={`${className}-card`}>
      <IconBadge className={className}>
        <img src={icon} alt="" />
      </IconBadge>
      <CardTitle data-testid={`ai-insights-${className}-title`}>{title}</CardTitle>
      {hasAnyItems ? (
        <div data-testid={`ai-insights-${className}-text`}>
          {SECTIONS.map((section) => {
            const items = insights[section.key] || [];
            if (items.length === 0) return null;
            return (
              <SectionWrapper key={section.key}>
                <SectionTitle>{section.title}</SectionTitle>
                <InsightList>
                  {items.map((item, index) => (
                    <InsightItem key={index}>{item}</InsightItem>
                  ))}
                </InsightList>
              </SectionWrapper>
            );
          })}
        </div>
      ) : (
        <EmptyText>No insights available</EmptyText>
      )}
    </Card>
  );
};

export default GroupedInsightCard;
